import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { toWeight } from '@rematter/modules/trade/utils/weight';
import { TouchNumberField } from '@rematter/ui-web';

import { ItemFieldProps } from '../_types';

import { useWeightConversion } from '~/modules/trade/hooks/useWeightConversion';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { useUIPreferences } from '~/shared/hooks';

//
// ⚛️ ⚛️ ⚛️ NET WEIGHT FIELD ⚛️ ⚛️ ⚛️
//
export const NetWeightField: React.FC<ItemFieldProps> = ({
  lineItem,
  disabled,
  onUpdateItem,
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
  onKeyDown,
}) => {
  // Translations
  const t = useTranslations();

  // Data hooks
  const { touchModeEnabled } = useUIPreferences();
  const { convertToDisplayUnit, convertFromDisplayUnit, massUnitAbbreviation } =
    useWeightConversion({
      weightUnit,
      facilityDefaultUnit,
      pricingUnit: (lineItem.pricingUnit as Unit) ?? enumUnit.Pounds,
    });

  // Memoized values
  const isQuantityBased = React.useMemo(
    () => lineItem.pricingUnit === enumUnit.Each,
    [lineItem.pricingUnit],
  );

  const netWeight = lineItem.weights?.[enumLoadWeightType.Net];
  const storedUnit = (netWeight?.unit as Unit) ?? enumUnit.Pounds;

  const displayValue = React.useMemo(() => {
    if (!netWeight) return 0;
    return convertToDisplayUnit(toWeight(netWeight.value, netWeight.unit));
  }, [netWeight, convertToDisplayUnit]);

  // Callbacks
  const handleNetWeightChange = React.useCallback(
    (value: number) => {
      const convertedValue = convertFromDisplayUnit(value ?? 0, storedUnit);
      const newNetWeight = toWeight(convertedValue, storedUnit);

      onUpdateItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Net]: newNetWeight,
        },
      });
    },
    [
      lineItem.id,
      lineItem.weights,
      storedUnit,
      convertFromDisplayUnit,
      onUpdateItem,
    ],
  );

  return (
    <TouchNumberField
      aria-label={t.scaleTicketPro.columns.netWeight}
      isDisabled={disabled}
      value={displayValue}
      onChange={handleNetWeightChange}
      onKeyDown={onKeyDown}
      formatOptions={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
      touchModeEnabled={touchModeEnabled}
      suffix={massUnitAbbreviation}
      prefix={
        isQuantityBased
          ? undefined
          : t.scaleTicketPro.itemsInterface.netWeightPrefix
      }
      className="@NetWeightField"
    />
  );
};
